import React, { useState, useEffect } from 'react';
import { 
  BarChart3, TrendingUp, Package, DollarSign, Calendar, 
  Settings, AlertCircle, CheckCircle, ShoppingCart, Percent,
  ArrowUp, ArrowDown, Minus, RefreshCw, Download, Filter,
  PieChart, Target, Calculator, Building2, Zap, Shield, Users,
  Wrench, Megaphone, MoreHorizontal
} from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import BackButton from '../components/BackButton';
import { useLanguage } from '../contexts/LanguageContext';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AdminReports = ({ onBack, user }) => {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useState('sales');
  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  
  // Report data
  const [salesReport, setSalesReport] = useState(null);
  const [stockReport, setStockReport] = useState(null);
  const [bestSellers, setBestSellers] = useState(null);
  const [profitability, setProfitability] = useState(null);
  const [businessParams, setBusinessParams] = useState({
    rent: 0,
    utilities: 0,
    insurance: 0,
    salaries: 0,
    marketing: 0,
    maintenance: 0,
    other_expenses: 0,
    target_profit_margin: 20
  });
  const [savingParams, setSavingParams] = useState(false);

  const token = localStorage.getItem('auth_token');

  // Fetch reports
  const fetchSalesReport = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      const res = await fetch(`${API_URL}/api/admin/reports/sales?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch sales report');
      const data = await res.json();
      setSalesReport(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchStockReport = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/stock`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch stock report');
      const data = await res.json();
      setStockReport(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBestSellers = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reports/best-sellers?days=30&limit=20`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch best sellers');
      const data = await res.json();
      setBestSellers(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchProfitability = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (dateRange.start) params.append('start_date', dateRange.start);
      if (dateRange.end) params.append('end_date', dateRange.end);
      
      const res = await fetch(`${API_URL}/api/admin/reports/profitability?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch profitability report');
      const data = await res.json();
      setProfitability(data);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBusinessParams = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/business-parameters`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!res.ok) throw new Error('Failed to fetch parameters');
      const data = await res.json();
      setBusinessParams(data);
    } catch (error) {
      console.error('Error fetching business params:', error);
    }
  };

  const saveBusinessParams = async () => {
    setSavingParams(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/business-parameters`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(businessParams)
      });
      if (!res.ok) throw new Error('Failed to save parameters');
      toast.success('Business parameters saved!');
      fetchProfitability();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSavingParams(false);
    }
  };

  // Load data on tab change
  useEffect(() => {
    if (activeTab === 'sales') fetchSalesReport();
    else if (activeTab === 'stock') fetchStockReport();
    else if (activeTab === 'best-sellers') fetchBestSellers();
    else if (activeTab === 'profitability') {
      fetchBusinessParams();
      fetchProfitability();
    }
    else if (activeTab === 'settings') fetchBusinessParams();
  }, [activeTab]);

  // Stat Card Component
  const StatCard = ({ title, value, subtitle, icon: Icon, trend, color = 'blue' }) => (
    <Card className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-gray-500 font-medium">{title}</p>
          <p className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
          {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
        </div>
        <div className={`w-12 h-12 rounded-xl bg-${color}-100 flex items-center justify-center`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`flex items-center gap-1 mt-3 text-sm ${trend >= 0 ? 'text-green-600' : 'text-red-600'}`}>
          {trend >= 0 ? <ArrowUp className="w-4 h-4" /> : <ArrowDown className="w-4 h-4" />}
          <span>{Math.abs(trend)}% vs target</span>
        </div>
      )}
    </Card>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <BackButton onClick={onBack} />
              <div>
                <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <BarChart3 className="w-7 h-7 text-blue-600" />
                  Reports & Analytics
                </h1>
                <p className="text-gray-500 text-sm">Business insights and performance metrics</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              onClick={() => {
                if (activeTab === 'sales') fetchSalesReport();
                else if (activeTab === 'stock') fetchStockReport();
                else if (activeTab === 'best-sellers') fetchBestSellers();
                else if (activeTab === 'profitability') fetchProfitability();
              }}
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 mb-6">
            <TabsTrigger value="sales" className="flex items-center gap-2">
              <DollarSign className="w-4 h-4" />
              <span className="hidden sm:inline">Sales</span>
            </TabsTrigger>
            <TabsTrigger value="stock" className="flex items-center gap-2">
              <Package className="w-4 h-4" />
              <span className="hidden sm:inline">Stock</span>
            </TabsTrigger>
            <TabsTrigger value="best-sellers" className="flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              <span className="hidden sm:inline">Best Sellers</span>
            </TabsTrigger>
            <TabsTrigger value="profitability" className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span className="hidden sm:inline">Profitability</span>
            </TabsTrigger>
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <Settings className="w-4 h-4" />
              <span className="hidden sm:inline">Costs</span>
            </TabsTrigger>
          </TabsList>

          {/* Sales Report */}
          <TabsContent value="sales">
            {/* Date Filter */}
            <Card className="p-4 mb-6">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <Button onClick={fetchSalesReport} disabled={loading}>
                  <Filter className="w-4 h-4 mr-2" />
                  Apply Filter
                </Button>
              </div>
            </Card>

            {salesReport && (
              <>
                {/* Summary Stats */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard
                    title="Total Revenue"
                    value={`$${salesReport.summary.total_revenue.toLocaleString()}`}
                    icon={DollarSign}
                    color="green"
                  />
                  <StatCard
                    title="Total Orders"
                    value={salesReport.summary.total_orders}
                    subtitle={`${salesReport.summary.completion_rate}% completed`}
                    icon={ShoppingCart}
                    color="blue"
                  />
                  <StatCard
                    title="Avg Order Value"
                    value={`$${salesReport.summary.average_order_value}`}
                    icon={Calculator}
                    color="purple"
                  />
                  <StatCard
                    title="Tax Collected"
                    value={`$${salesReport.summary.total_tax_collected}`}
                    icon={Percent}
                    color="orange"
                  />
                </div>

                {/* Order Status */}
                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  <Card className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">Order Status</h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-green-500" />
                          Completed
                        </span>
                        <span className="font-semibold">{salesReport.summary.completed_orders}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-yellow-500" />
                          Pending
                        </span>
                        <span className="font-semibold">{salesReport.summary.pending_orders}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full bg-red-500" />
                          Cancelled
                        </span>
                        <span className="font-semibold">{salesReport.summary.cancelled_orders}</span>
                      </div>
                    </div>
                  </Card>

                  <Card className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">Payment Methods</h3>
                    <div className="space-y-3">
                      {Object.entries(salesReport.by_payment_method).map(([method, data]) => (
                        <div key={method} className="flex justify-between items-center">
                          <span className="capitalize">{method.replace('_', ' ')}</span>
                          <div className="text-right">
                            <span className="font-semibold">${data.total.toFixed(2)}</span>
                            <span className="text-gray-500 text-sm ml-2">({data.count} orders)</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>
                </div>

                {/* Top Products */}
                <Card className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Top Selling Products</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-sm font-medium text-gray-500">Product</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Qty Sold</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {salesReport.top_products.map((product, i) => (
                          <tr key={i} className="border-b last:border-0">
                            <td className="py-3">{product.name}</td>
                            <td className="py-3 text-right">{product.quantity}</td>
                            <td className="py-3 text-right font-semibold">${product.revenue.toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Stock Report */}
          <TabsContent value="stock">
            {stockReport && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard
                    title="Total Products"
                    value={stockReport.summary.total_products}
                    icon={Package}
                    color="blue"
                  />
                  <StatCard
                    title="In Stock"
                    value={stockReport.summary.in_stock}
                    subtitle={`${stockReport.summary.stock_rate}% available`}
                    icon={CheckCircle}
                    color="green"
                  />
                  <StatCard
                    title="Out of Stock"
                    value={stockReport.summary.out_of_stock}
                    icon={AlertCircle}
                    color="red"
                  />
                  <StatCard
                    title="Inventory Value"
                    value={`$${stockReport.summary.estimated_inventory_value.toLocaleString()}`}
                    icon={DollarSign}
                    color="purple"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* By Category */}
                  <Card className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">Stock by Category</h3>
                    <div className="space-y-3">
                      {Object.entries(stockReport.by_category).map(([category, data]) => (
                        <div key={category} className="flex items-center justify-between">
                          <span className="capitalize">{category}</span>
                          <div className="flex items-center gap-3">
                            <div className="w-24 bg-gray-200 rounded-full h-2">
                              <div 
                                className="bg-green-500 h-2 rounded-full" 
                                style={{width: `${(data.in_stock / data.total) * 100}%`}}
                              />
                            </div>
                            <span className="text-sm text-gray-600 w-16 text-right">
                              {data.in_stock}/{data.total}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </Card>

                  {/* Out of Stock Items */}
                  <Card className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <AlertCircle className="w-5 h-5 text-red-500" />
                      Out of Stock Items
                    </h3>
                    {stockReport.out_of_stock_items.length === 0 ? (
                      <p className="text-gray-500 text-center py-8">All products are in stock!</p>
                    ) : (
                      <div className="space-y-2 max-h-64 overflow-y-auto">
                        {stockReport.out_of_stock_items.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-2 bg-red-50 rounded-lg">
                            <span className="font-medium">{item.name}</span>
                            <Badge variant="outline" className="text-xs">{item.category}</Badge>
                          </div>
                        ))}
                      </div>
                    )}
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Best Sellers */}
          <TabsContent value="best-sellers">
            {bestSellers && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                  <StatCard
                    title="Period"
                    value={bestSellers.period}
                    icon={Calendar}
                    color="blue"
                  />
                  <StatCard
                    title="Units Sold"
                    value={bestSellers.total_products_sold}
                    icon={Package}
                    color="green"
                  />
                  <StatCard
                    title="Total Revenue"
                    value={`$${bestSellers.total_revenue.toLocaleString()}`}
                    icon={DollarSign}
                    color="purple"
                  />
                </div>

                <Card className="p-5">
                  <h3 className="font-semibold text-gray-900 mb-4">Best Selling Products</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2 text-sm font-medium text-gray-500">Rank</th>
                          <th className="text-left py-2 text-sm font-medium text-gray-500">Product</th>
                          <th className="text-left py-2 text-sm font-medium text-gray-500">Category</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Units</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Orders</th>
                          <th className="text-right py-2 text-sm font-medium text-gray-500">Revenue</th>
                        </tr>
                      </thead>
                      <tbody>
                        {bestSellers.best_sellers.map((product) => (
                          <tr key={product.product_id} className="border-b last:border-0 hover:bg-gray-50">
                            <td className="py-3">
                              <Badge variant={product.rank <= 3 ? 'default' : 'outline'} 
                                className={product.rank === 1 ? 'bg-yellow-500' : product.rank === 2 ? 'bg-gray-400' : product.rank === 3 ? 'bg-orange-400' : ''}>
                                #{product.rank}
                              </Badge>
                            </td>
                            <td className="py-3 font-medium">{product.name}</td>
                            <td className="py-3 capitalize text-gray-500">{product.category}</td>
                            <td className="py-3 text-right">{product.units_sold}</td>
                            <td className="py-3 text-right">{product.orders_count}</td>
                            <td className="py-3 text-right font-semibold text-green-600">${product.revenue}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Card>
              </>
            )}
          </TabsContent>

          {/* Profitability */}
          <TabsContent value="profitability">
            {/* Date Filter */}
            <Card className="p-4 mb-6">
              <div className="flex flex-wrap items-end gap-4">
                <div>
                  <Label>Start Date</Label>
                  <Input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Date</Label>
                  <Input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
                    className="mt-1"
                  />
                </div>
                <Button onClick={fetchProfitability} disabled={loading}>
                  <Filter className="w-4 h-4 mr-2" />
                  Calculate
                </Button>
              </div>
            </Card>

            {profitability && (
              <>
                {/* Key Metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                  <StatCard
                    title="Net Revenue"
                    value={`$${profitability.revenue.net_revenue.toLocaleString()}`}
                    subtitle="After tax"
                    icon={DollarSign}
                    color="blue"
                  />
                  <StatCard
                    title="Period Overhead"
                    value={`$${profitability.costs.period_overhead.toLocaleString()}`}
                    subtitle={`$${profitability.costs.cost_per_order}/order`}
                    icon={Building2}
                    color="orange"
                  />
                  <StatCard
                    title="Gross Profit"
                    value={`$${profitability.profitability.gross_profit.toLocaleString()}`}
                    icon={TrendingUp}
                    color={profitability.profitability.gross_profit >= 0 ? 'green' : 'red'}
                  />
                  <StatCard
                    title="Profit Margin"
                    value={`${profitability.profitability.profit_margin}%`}
                    subtitle={`Target: ${profitability.profitability.target_margin}%`}
                    icon={Target}
                    color={profitability.profitability.margin_vs_target >= 0 ? 'green' : 'red'}
                    trend={profitability.profitability.margin_vs_target}
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-6 mb-6">
                  {/* Cost Breakdown */}
                  <Card className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">Monthly Cost Breakdown</h3>
                    <div className="space-y-3">
                      {[
                        { label: 'Rent', value: profitability.costs.breakdown.rent, icon: Building2 },
                        { label: 'Utilities', value: profitability.costs.breakdown.utilities, icon: Zap },
                        { label: 'Insurance', value: profitability.costs.breakdown.insurance, icon: Shield },
                        { label: 'Salaries', value: profitability.costs.breakdown.salaries, icon: Users },
                        { label: 'Marketing', value: profitability.costs.breakdown.marketing, icon: Megaphone },
                        { label: 'Maintenance', value: profitability.costs.breakdown.maintenance, icon: Wrench },
                        { label: 'Other', value: profitability.costs.breakdown.other, icon: MoreHorizontal },
                      ].map(({ label, value, icon: Icon }) => (
                        <div key={label} className="flex items-center justify-between">
                          <span className="flex items-center gap-2 text-gray-600">
                            <Icon className="w-4 h-4" />
                            {label}
                          </span>
                          <span className="font-semibold">${value.toLocaleString()}</span>
                        </div>
                      ))}
                      <div className="border-t pt-3 flex items-center justify-between font-bold">
                        <span>Total Monthly</span>
                        <span className="text-lg">${profitability.costs.monthly_overhead.toLocaleString()}</span>
                      </div>
                    </div>
                  </Card>

                  {/* Break-Even Analysis */}
                  <Card className="p-5">
                    <h3 className="font-semibold text-gray-900 mb-4">Break-Even Analysis</h3>
                    <div className="space-y-4">
                      <div className="p-4 bg-blue-50 rounded-xl">
                        <p className="text-sm text-blue-600 mb-1">Orders Needed (Monthly)</p>
                        <p className="text-3xl font-bold text-blue-700">
                          {profitability.break_even.monthly_orders_needed}
                        </p>
                        <p className="text-sm text-blue-600 mt-1">
                          ~{profitability.break_even.daily_orders_needed} orders/day
                        </p>
                      </div>
                      
                      <div className="p-4 bg-gray-50 rounded-xl">
                        <p className="text-sm text-gray-600 mb-1">Current Orders (This Period)</p>
                        <p className="text-2xl font-bold text-gray-800">
                          {profitability.orders.total}
                        </p>
                        <p className="text-sm text-gray-600 mt-1">
                          Avg: ${profitability.orders.average_value}/order
                        </p>
                      </div>

                      <div className={`p-4 rounded-xl ${profitability.profitability.gross_profit >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className={`text-sm ${profitability.profitability.gross_profit >= 0 ? 'text-green-600' : 'text-red-600'} mb-1`}>
                          Status
                        </p>
                        <p className={`text-lg font-bold ${profitability.profitability.gross_profit >= 0 ? 'text-green-700' : 'text-red-700'}`}>
                          {profitability.profitability.gross_profit >= 0 ? 'Profitable' : 'Below Break-Even'}
                        </p>
                      </div>
                    </div>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Business Parameters Settings */}
          <TabsContent value="settings">
            <Card className="p-6">
              <h3 className="font-semibold text-gray-900 mb-6 flex items-center gap-2">
                <Calculator className="w-5 h-5" />
                Business Operating Costs (Monthly)
              </h3>
              <p className="text-gray-500 text-sm mb-6">
                Enter your monthly business expenses to calculate accurate profitability and cost of goods sold.
              </p>

              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <Label className="flex items-center gap-2">
                    <Building2 className="w-4 h-4 text-gray-400" />
                    Monthly Rent
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      value={businessParams.rent}
                      onChange={(e) => setBusinessParams({...businessParams, rent: parseFloat(e.target.value) || 0})}
                      className="pl-8"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Zap className="w-4 h-4 text-gray-400" />
                    Utilities (Power, Water, Gas)
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      value={businessParams.utilities}
                      onChange={(e) => setBusinessParams({...businessParams, utilities: parseFloat(e.target.value) || 0})}
                      className="pl-8"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-gray-400" />
                    Insurance
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      value={businessParams.insurance}
                      onChange={(e) => setBusinessParams({...businessParams, insurance: parseFloat(e.target.value) || 0})}
                      className="pl-8"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Users className="w-4 h-4 text-gray-400" />
                    Salaries & Wages
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      value={businessParams.salaries}
                      onChange={(e) => setBusinessParams({...businessParams, salaries: parseFloat(e.target.value) || 0})}
                      className="pl-8"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Megaphone className="w-4 h-4 text-gray-400" />
                    Marketing & Advertising
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      value={businessParams.marketing}
                      onChange={(e) => setBusinessParams({...businessParams, marketing: parseFloat(e.target.value) || 0})}
                      className="pl-8"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Wrench className="w-4 h-4 text-gray-400" />
                    Maintenance & Repairs
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      value={businessParams.maintenance}
                      onChange={(e) => setBusinessParams({...businessParams, maintenance: parseFloat(e.target.value) || 0})}
                      className="pl-8"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <MoreHorizontal className="w-4 h-4 text-gray-400" />
                    Other Expenses
                  </Label>
                  <div className="relative mt-1">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                    <Input
                      type="number"
                      value={businessParams.other_expenses}
                      onChange={(e) => setBusinessParams({...businessParams, other_expenses: parseFloat(e.target.value) || 0})}
                      className="pl-8"
                      placeholder="0"
                    />
                  </div>
                </div>

                <div>
                  <Label className="flex items-center gap-2">
                    <Target className="w-4 h-4 text-gray-400" />
                    Target Profit Margin (%)
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      type="number"
                      value={businessParams.target_profit_margin}
                      onChange={(e) => setBusinessParams({...businessParams, target_profit_margin: parseFloat(e.target.value) || 0})}
                      className="pr-8"
                      placeholder="20"
                      min="0"
                      max="100"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">%</span>
                  </div>
                </div>
              </div>

              {/* Total */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center justify-between">
                  <span className="font-semibold text-gray-700">Total Monthly Overhead</span>
                  <span className="text-2xl font-bold text-gray-900">
                    ${(
                      (businessParams.rent || 0) +
                      (businessParams.utilities || 0) +
                      (businessParams.insurance || 0) +
                      (businessParams.salaries || 0) +
                      (businessParams.marketing || 0) +
                      (businessParams.maintenance || 0) +
                      (businessParams.other_expenses || 0)
                    ).toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={saveBusinessParams} disabled={savingParams} className="px-8">
                  {savingParams ? 'Saving...' : 'Save Parameters'}
                </Button>
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminReports;
